import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, map, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { getDistinctLocationCodes, GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Roll, RollCategory } from '@app/models/knitting/roll';
import { WorkOrder, WorkOrderSearchCommand } from '@app/models/order/work-order';
import { RollsReassignmentCommand } from '@app/models/production/commands/roll-reassignment-command';
import { DyeingColor } from '@app/models/production/dyeing/dyeing-color';
import { ProductionCard, ProductionCardTransferCheckResult } from '@app/models/production/production-card';
import { LabDipRequestProxyService } from '@app/proxies/item/lab-dip-request-proxy.service';
import { WorkOrderProxyService } from '@app/proxies/order/work-order.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { LocationPathService } from '@app/services/location-path.service';

export interface RollFilters {
  rollCategory: RollCategory | null;
  rollNumber: string;
  fabric: string | null;
  quality: number | null;
  location: string | null;
}

const EMPTY_FILTERS: RollFilters = {
  rollCategory: null,
  rollNumber: '',
  fabric: null,
  quality: null,
  location: null,
};

// Substring used across the app to identify a Finished Goods warehouse location code (e.g. NT.WH-FG-1)
const FINISHED_GOODS_LOCATION_MARKER = 'WH-FG';

@Component({
  selector: 'app-reassign-rolls',
  templateUrl: './reassign-rolls.component.html',
  styleUrl: './reassign-rolls.component.scss',
})
export class ReassignRollsComponent implements OnInit {
  private readonly rollProxy = inject(RollProxyService);
  private readonly productionCardProxy = inject(ProductionCardsProxyService);
  private readonly workOrderProxy = inject(WorkOrderProxyService);
  private readonly labDipRequestProxy = inject(LabDipRequestProxyService);
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly enumService = inject(EnumService);
  private readonly locationPathService = inject(LocationPathService);

  // Reactive Form
  transferForm!: FormGroup;

  // Loading state
  isLoading = 0;

  // Colors map for display
  colorsMap = signal<Map<string, DyeingColor>>(new Map());

  // Source Work Order

  // Origin Production Cards (from selected source work order)
  originProductionCardOptions = signal<ProductionCard[]>([]);

  // Transfer check result
  transferCheckResult = signal<ProductionCardTransferCheckResult | null>(null);

  // Rolls from transfer check
  rolls = signal<Roll[]>([]);
  selectedRolls = signal<Roll[]>([]);

  // Target Work Order

  // Target Production Card
  targetProductionCardOptions = signal<ProductionCard[]>([]);

  // ========== FILTERS ==========
  filters = signal<RollFilters>({ ...EMPTY_FILTERS });
  rollCategoryFilterOptions: { label: string; value: number }[] = [];
  rollQualityFilterOptions: { label: string; value: number }[] = [];

  filteredRolls = computed(() => {
    const filters = this.filters();
    return this.rolls().filter((roll) => {
      if (filters.rollCategory !== null && roll.rollCategory !== filters.rollCategory) return false;
      if (filters.rollNumber && !roll.number?.toLowerCase().includes(filters.rollNumber.toLowerCase())) return false;
      if (filters.fabric && roll.expectedFabricCode !== filters.fabric) return false;
      if (filters.quality !== null && roll.rollQuality !== filters.quality) return false;
      if (filters.location && roll.currentLocationCode !== filters.location) return false;
      return true;
    });
  });

  fabricFilterOptions = computed(() => {
    const codes = new Set(
      this.rolls()
        .map((roll) => roll.expectedFabricCode)
        .filter((code): code is string => !!code),
    );
    return Array.from(codes).sort();
  });

  // Value stays the location code (the filter compares codes); the label is the full location path.
  locationFilterOptions = computed(() => {
    return getDistinctLocationCodes(this.rolls(), (roll) => roll.currentLocationCode)
      .map((code) => ({ label: this.locationPathService.label(code), value: code }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  private readonly loadLocationPaths = effect(() => {
    this.locationPathService.load(this.rolls().map((roll) => roll.currentLocationCode));
  });

  // ========== FINISHED GOODS GATE ==========
  disabledRollNumbers = computed(() => {
    return this.rolls()
      .filter((roll) => this.isRollInFinishedGoods(roll))
      .map((roll) => roll.number)
      .filter((number): number is string => !!number);
  });

  // Computed: Options for p-select (origin production card)
  originProductionCardSelectOptions = computed(() => {
    const colors = this.colorsMap();
    return this.originProductionCardOptions().map((pc) => {
      let label = pc.productionCardNumber || '';
      if (pc.colorReference) {
        const color = colors.get(pc.colorReference);
        const colorName = color?.name || pc.colorReference;
        label += ` - ${colorName}`;
      }
      return {
        label,
        value: pc,
      };
    });
  });

  // Computed: Options for p-select (target production card)
  targetProductionCardSelectOptions = computed(() => {
    const colors = this.colorsMap();
    return this.targetProductionCardOptions().map((pc) => {
      let label = pc.productionCardNumber || '';
      if (pc.colorReference) {
        const color = colors.get(pc.colorReference);
        const colorName = color?.name || pc.colorReference;
        label += ` - ${colorName}`;
      }
      return {
        label,
        value: pc,
      };
    });
  });

  // Computed validations
  canReassign = computed(() => {
    return (
      this.selectedRolls().length > 0 &&
      this.transferForm?.get('targetProductionCard')?.value !== null &&
      this.isLoading === 0 &&
      this.transferCheckResult() !== null &&
      this.transferCheckResult()?.colorReferenceMatches === true &&
      this.transferCheckResult()?.fabricCodesCompatible === true
    );
  });

  // Show table only when transfer check has been done
  showTable = computed(() => {
    return this.transferCheckResult() !== null;
  });

  constructor() {
    const rollCategoryEnum = this.enumService.getEnum('RollCategory', 'productionEnums')?.values || {};
    this.rollCategoryFilterOptions = Object.entries(rollCategoryEnum).map(([key, label]) => ({
      label: label as string,
      value: Number(key),
    }));

    const rollQualityEnum = this.enumService.getEnum('RollQuality', 'productionEnums')?.values || {};
    this.rollQualityFilterOptions = Object.entries(rollQualityEnum).map(([key, label]) => ({
      label: label as string,
      value: Number(key),
    }));
  }

  ngOnInit(): void {
    this.initForm();
    this.loadFromQueryParams();
  }

  private initForm(): void {
    this.transferForm = this.fb.group({
      sourceWorkOrder: [null, Validators.required],
      originProductionCard: [null, Validators.required],
      targetWorkOrder: [null, Validators.required],
      targetProductionCard: [null, Validators.required],
    });

    // Subscribe to form changes to reset dependent fields
    this.transferForm.get('sourceWorkOrder')?.valueChanges.subscribe(() => {
      this.transferForm.patchValue({
        originProductionCard: null,
        targetWorkOrder: null,
        targetProductionCard: null,
      });
      this.originProductionCardOptions.set([]);
      this.targetProductionCardOptions.set([]);
      this.transferCheckResult.set(null);
      this.resetRollsState();
    });

    this.transferForm.get('originProductionCard')?.valueChanges.subscribe(() => {
      this.transferForm.patchValue({
        targetWorkOrder: null,
        targetProductionCard: null,
      });
      this.targetProductionCardOptions.set([]);
      this.transferCheckResult.set(null);
      this.resetRollsState();
    });

    this.transferForm.get('targetWorkOrder')?.valueChanges.subscribe(() => {
      this.transferForm.patchValue({
        targetProductionCard: null,
      });
      this.targetProductionCardOptions.set([]);
      this.transferCheckResult.set(null);
      this.resetRollsState();
    });

    this.transferForm.get('targetProductionCard')?.valueChanges.subscribe(() => {
      this.transferCheckResult.set(null);
      this.resetRollsState();
    });
  }

  // ========== SOURCE WORK ORDER SEARCH ==========

  public fetchSourceWorkOrders = (query: string): Observable<WorkOrder[]> =>
    this.workOrderProxy
      .search({ orderNumber: query } as WorkOrderSearchCommand, 1, 50, 'orderNumber', 1)
      .pipe(map((response) => response.data?.items ?? []));

  onSourceWorkOrderSelected(event: AutoCompleteSelectEvent): void {
    const workOrder = event.value as WorkOrder;
    this.transferForm.patchValue({ sourceWorkOrder: workOrder });
    this.loadOriginProductionCards();
  }

  clearSourceWorkOrder(): void {
    this.transferForm.patchValue({ sourceWorkOrder: null });
  }

  // ========== ORIGIN PRODUCTION CARD LOADING ==========

  private loadOriginProductionCards(): void {
    const workOrder = this.transferForm.get('sourceWorkOrder')?.value as WorkOrder;
    if (!workOrder?.id) return;

    this.isLoading++;
    this.productionCardProxy.getProductionCardsByWorkOrderId(workOrder.id, true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.originProductionCardOptions.set(response.data);
          this.loadColorsForProductionCards(response.data);
        } else {
          this.originProductionCardOptions.set([]);
        }
        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
        this.originProductionCardOptions.set([]);
      },
    });
  }

  onOriginProductionCardSelected(productionCard: ProductionCard | null): void {
    this.transferForm.patchValue({ originProductionCard: productionCard });
  }

  // ========== TARGET WORK ORDER SEARCH ==========

  public fetchTargetWorkOrders = (query: string): Observable<WorkOrder[]> =>
    this.workOrderProxy.search({ orderNumber: query } as WorkOrderSearchCommand, 1, 50).pipe(map((response) => response.data?.items ?? []));

  onTargetWorkOrderSelected(event: AutoCompleteSelectEvent): void {
    const workOrder = event.value as WorkOrder;
    this.transferForm.patchValue({ targetWorkOrder: workOrder });
    this.loadTargetProductionCards();
  }

  clearTargetWorkOrder(): void {
    this.transferForm.patchValue({ targetWorkOrder: null });
  }

  // ========== TARGET PRODUCTION CARD LOADING ==========

  private loadTargetProductionCards(): void {
    const workOrder = this.transferForm.get('targetWorkOrder')?.value as WorkOrder;
    if (!workOrder?.id) return;

    this.isLoading++;
    this.productionCardProxy.getProductionCardsByWorkOrderId(workOrder.id).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.targetProductionCardOptions.set(response.data);
          this.loadColorsForProductionCards(response.data);
        } else {
          this.targetProductionCardOptions.set([]);
        }
        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
        this.targetProductionCardOptions.set([]);
      },
    });
  }

  onTargetProductionCardSelected(productionCard: ProductionCard | null): void {
    this.transferForm.patchValue({ targetProductionCard: productionCard });
  }

  // ========== COLOR LOADING ==========

  private loadColorsForProductionCards(productionCards: ProductionCard[]): void {
    const colorReferences = productionCards
      .map((pc) => pc.colorReference)
      .filter((ref): ref is string => !!ref)
      .filter((ref, index, self) => self.indexOf(ref) === index);

    if (colorReferences.length === 0) return;

    this.isLoading++;
    this.labDipRequestProxy.getColorsByIds(colorReferences).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const newColorsMap = new Map(this.colorsMap());
          response.data.forEach((color) => {
            newColorsMap.set(color.id, color);
          });
          this.colorsMap.set(newColorsMap);
        }
        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  // ========== TRANSFER CHECK ==========

  checkTransfer(): void {
    if (this.transferForm.invalid) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Warning'),
        this.translateService.instant('Reassign_Rolls_Toast_FillRequiredFields'),
      );
      return;
    }

    const originProductionCard = this.transferForm.get('originProductionCard')?.value as ProductionCard;
    const targetProductionCard = this.transferForm.get('targetProductionCard')?.value as ProductionCard;

    if (!originProductionCard?.id || !targetProductionCard?.id) {
      GlobalMessageUtil.error(
        this.translateService.instant('Error'),
        this.translateService.instant('Reassign_Rolls_Toast_InvalidProductionCards'),
      );
      return;
    }

    this.isLoading++;
    this.productionCardProxy
      .checkTransfer({
        originProductionCardReference: originProductionCard.id,
        targetProductionCardReference: targetProductionCard.id,
      })
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.transferCheckResult.set(response.data);
            this.rolls.set(response.data.rolls);
            this.selectedRolls.set([]);
            this.filters.set({ ...EMPTY_FILTERS });

            // Show validation messages
            if (!response.data.colorReferenceMatches) {
              GlobalMessageUtil.warn(
                this.translateService.instant('Warning'),
                this.translateService.instant('Reassign_Rolls_Toast_ColorMismatch'),
              );
            }
            if (!response.data.fabricCodesCompatible) {
              const incompatible = response.data.incompatibleFabricCodes?.join(', ') || 'unknown';
              const targetCodes = response.data.targetFabricCodes.join(', ');
              GlobalMessageUtil.warn(
                this.translateService.instant('Warning'),
                this.translateService
                  .instant('Reassign_Rolls_Toast_FabricIncompatible')
                  .replace('{0}', incompatible)
                  .replace('{1}', targetCodes),
              );
            }
            if (response.data.colorReferenceMatches && response.data.fabricCodesCompatible) {
              GlobalMessageUtil.success(
                this.translateService.instant('Success'),
                this.translateService.instant('Reassign_Rolls_Toast_ValidationPassed'),
              );
            }
          }
          this.isLoading--;
        },
        error: () => {
          this.isLoading--;
          this.transferCheckResult.set(null);
          this.resetRollsState();
        },
      });
  }

  // ========== ROLL SELECTION ==========

  onRollSelectionChange(selection: Roll[]): void {
    this.selectedRolls.set(selection);
  }

  // ========== REASSIGNMENT ==========

  performReassignment(): void {
    if (!this.canReassign()) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Warning'),
        this.translateService.instant('Reassign_Rolls_Toast_CannotReassign'),
      );
      return;
    }

    const selectedRolls = this.selectedRolls();
    const targetProductionCard = this.transferForm.get('targetProductionCard')?.value as ProductionCard;

    if (!targetProductionCard?.id) {
      GlobalMessageUtil.error(
        this.translateService.instant('Error'),
        this.translateService.instant('Reassign_Rolls_Toast_InvalidTargetPC'),
      );
      return;
    }

    const command: RollsReassignmentCommand = {
      rollNumbers: selectedRolls.map((r) => r.number!).filter((n): n is string => !!n),
      targetProductionCardReference: targetProductionCard.id,
    };

    this.isLoading++;
    this.rollProxy.reassignRolls(command).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          GlobalMessageUtil.success(
            this.translateService.instant('Success'),
            this.translateService
              .instant('Reassign_Rolls_Toast_ReassignSuccess')
              .replace('{0}', response.data.length.toString())
              .replace('{1}', targetProductionCard.productionCardNumber || ''),
          );
          // Re-run transfer check to refresh rolls
          this.checkTransfer();
        }
        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  // ========== FILTERS ==========

  updateFilter(patch: Partial<RollFilters>): void {
    this.filters.update((current) => ({ ...current, ...patch }));
  }

  clearFilters(): void {
    this.filters.set({ ...EMPTY_FILTERS });
  }

  // ========== FINISHED GOODS GATE ==========

  isRollInFinishedGoods(roll: Roll): boolean {
    return !!roll.currentLocationCode?.toUpperCase().includes(FINISHED_GOODS_LOCATION_MARKER);
  }

  private resetRollsState(): void {
    this.rolls.set([]);
    this.selectedRolls.set([]);
    this.filters.set({ ...EMPTY_FILTERS });
  }

  // ========== PRELOAD FROM URL QUERY PARAMS ==========
  // Optional deep-link: /reassign-rolls?originPcId=<id>&targetPcId=<id>
  // Resolves each Production Card's Work Order and preloads the cascading form so the user
  // only has to click "Check Transfer Compatibility". No-op (current manual flow) if the
  // params are absent.

  private loadFromQueryParams(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      const originPcId = params['originPcId'];
      const targetPcId = params['targetPcId'];
      if (originPcId && targetPcId) {
        this.preloadFromProductionCardIds(originPcId, targetPcId);
      }
    });
  }

  private preloadFromProductionCardIds(originPcId: string, targetPcId: string): void {
    this.isLoading++;
    forkJoin({
      originProductionCard: this.productionCardProxy.getProductionCardByID(originPcId),
      targetProductionCard: this.productionCardProxy.getProductionCardByID(targetPcId),
    }).subscribe({
      next: ({ originProductionCard, targetProductionCard }) => {
        if (!originProductionCard.success || !originProductionCard.data || !targetProductionCard.success || !targetProductionCard.data) {
          this.isLoading--;
          GlobalMessageUtil.error(
            this.translateService.instant('Error'),
            this.translateService.instant('Reassign_Rolls_Toast_InvalidProductionCards'),
          );
          return;
        }

        const originPc = originProductionCard.data;
        const targetPc = targetProductionCard.data;

        forkJoin({
          sourceWorkOrder: this.workOrderProxy.getById(originPc.workOrderReference),
          targetWorkOrder: this.workOrderProxy.getById(targetPc.workOrderReference),
        }).subscribe({
          next: ({ sourceWorkOrder, targetWorkOrder }) => {
            this.isLoading--;
            if (!sourceWorkOrder.success || !sourceWorkOrder.data || !targetWorkOrder.success || !targetWorkOrder.data) {
              GlobalMessageUtil.error(
                this.translateService.instant('Error'),
                this.translateService.instant('Reassign_Rolls_Toast_InvalidProductionCards'),
              );
              return;
            }
            this.preloadForm(sourceWorkOrder.data, originPc, targetWorkOrder.data, targetPc);
          },
          error: () => {
            this.isLoading--;
          },
        });
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  // Patches the cascading form top-down with emitEvent:false (so the valueChanges resets don't
  // wipe the preload), then loads each side's Production Card options the same way the manual
  // selection flow does.
  private preloadForm(
    sourceWorkOrder: WorkOrder,
    originProductionCard: ProductionCard,
    targetWorkOrder: WorkOrder,
    targetProductionCard: ProductionCard,
  ): void {
    this.transferForm.patchValue({ sourceWorkOrder, targetWorkOrder }, { emitEvent: false });

    this.isLoading++;
    this.productionCardProxy.getProductionCardsByWorkOrderId(sourceWorkOrder.id, true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.originProductionCardOptions.set(response.data);
          const matchedOriginPc = response.data.find((pc) => pc.id === originProductionCard.id) ?? originProductionCard;
          this.transferForm.patchValue({ originProductionCard: matchedOriginPc }, { emitEvent: false });
          this.loadColorsForProductionCards(response.data);
        }
        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
      },
    });

    this.isLoading++;
    this.productionCardProxy.getProductionCardsByWorkOrderId(targetWorkOrder.id).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.targetProductionCardOptions.set(response.data);
          const matchedTargetPc = response.data.find((pc) => pc.id === targetProductionCard.id) ?? targetProductionCard;
          this.transferForm.patchValue({ targetProductionCard: matchedTargetPc }, { emitEvent: false });
          this.loadColorsForProductionCards(response.data);
        }
        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
      },
    });
  }
}
